import type { DailyScore } from '../lib/supabase'

interface Props {
  latestScore: DailyScore | null
  weights: Record<string, number>
  thresholds: { entry: number; strong: number; exit: number }
}

const COMPONENTS = [
  { key: 'trend_direction', label: 'Trend Direction', weight: 0.25 },
  { key: 'trend_strength', label: 'Trend Strength', weight: 0.20 },
  { key: 'momentum', label: 'Momentum', weight: 0.20 },
  { key: 'volume', label: 'Volume', weight: 0.15 },
  { key: 'relative_strength', label: 'Relative Str.', weight: 0.20 },
] as const

function scoreColor(val: number): string {
  if (val >= 75) return 'var(--positive)'
  if (val >= 50) return 'var(--accent-cyan)'
  if (val >= 35) return 'var(--warning)'
  return 'var(--negative)'
}

function zoneLabel(score: number, thresholds: { entry: number; strong: number; exit: number }): { text: string; color: string } {
  if (score >= thresholds.strong) return { text: 'STRONG', color: 'var(--positive)' }
  if (score >= thresholds.entry) return { text: 'ENTRY ZONE', color: 'var(--accent-cyan)' }
  if (score >= thresholds.exit) return { text: 'NEUTRAL', color: 'var(--warning)' }
  return { text: 'EXIT ZONE', color: 'var(--negative)' }
}

export function TrendQualBreakdown({ latestScore, weights, thresholds }: Props) {
  if (!latestScore) {
    return (
      <div className="tq-breakdown">
        <div className="tq-breakdown-header">
          <span className="tq-breakdown-title">TrendQual Breakdown</span>
        </div>
        <div className="empty-state" style={{ padding: 'var(--space-lg)' }}>
          <div className="empty-state-text">No score data available</div>
        </div>
      </div>
    )
  }

  const zone = zoneLabel(latestScore.score, thresholds)

  return (
    <div className="tq-breakdown">
      <div className="tq-breakdown-header">
        <span className="tq-breakdown-title">TrendQual Breakdown</span>
        <span className="tq-breakdown-date">{latestScore.date}</span>
      </div>

      <div className="tq-bars">
        {COMPONENTS.map(comp => {
          const val = latestScore[comp.key as keyof DailyScore] as number
          const weightPct = (weights[comp.key] || comp.weight) * 100

          return (
            <div key={comp.key} className="tq-bar-row">
              <div className="tq-bar-label">
                <span>{comp.label}</span>
                <span className="tq-bar-weight">({weightPct}%)</span>
              </div>
              <div className="tq-bar-track">
                <div
                  className="tq-bar-fill"
                  style={{
                    width: `${Math.min(val, 100)}%`,
                    background: scoreColor(val),
                  }}
                />
              </div>
              <div className="tq-bar-value" style={{ color: scoreColor(val) }}>
                {val.toFixed(0)}
              </div>
            </div>
          )
        })}
      </div>

      <div className="tq-composite">
        <span className="tq-composite-label">Composite:</span>
        <span
          className="tq-composite-value"
          style={{
            color: zone.color,
            textShadow: latestScore.score >= 80 ? `0 0 8px ${zone.color}` : 'none',
          }}
        >
          {latestScore.score.toFixed(1)}
        </span>
        <span className="tq-composite-zone" style={{ color: zone.color }}>
          [{zone.text}]
        </span>
      </div>
    </div>
  )
}
